import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { MessageDescriptor } from 'react-intl';
import { handleApiException } from '../../global/modal/alerts-host/alert-templates';
import { IZoomDAO, IZoomGroupDTO } from '../../../api/zoom.dao';
import { getAddLeastOneGroupError } from '../../../constants/validation-errors';
import { AccountAdminSettingEntity } from '../common/account-admin-setting.entity';
import { RECURRING_ZOOM_MEETINGS_STATUS_UPDATED_TEXT } from '../account-admin.text';
import { BaseAccountAdminSettingViewModel } from '../common/base-account-admin-setting-form.viewmodel';

class RecurringZoomMeetingsFormVM extends BaseAccountAdminSettingViewModel {
    @mobx.observable
    public isLoading: boolean = false;
    @mobx.observable
    public zoomGroups: IZoomGroupDTO[] = [];
    @mobx.observable
    public isFormChecked: boolean = false;

    constructor(
        protected readonly entity: AccountAdminSettingEntity,
        @IoC.Inject private readonly zoomDAO: IZoomDAO = IoC.Container.get(IZoomDAO),

    ) {
        super(entity);
        this.fetchZoomGroups();
    }

    @mobx.action
    public fetchZoomGroups = async (): Promise<void> => {
        try {
            mobx.runInAction(() => {
                this.isLoading = true;
            });

            const response = await this.zoomDAO.getGroups();
            mobx.runInAction(() => {
                this.zoomGroups = response;
            });
        } catch (error) {
            handleApiException(error);
        } finally {
            mobx.runInAction(() => {
                this.isLoading = false;
            });
        }
    };

    @mobx.action
    public toggleRecurringZoomMeetingsEnabled = (): void => {
        this.entity.toggleEnabled();

        if (!this.entity.enabled) {
            this.isFormChecked = false;
        }
    };

    public getRecurringZoomMeetingsValidationError = (): string | null => {
        return (this.entity.enabled && (this.entity.additionalData as IZoomGroupDTO[])?.length === 0) ? getAddLeastOneGroupError(this.intl) : null;
    };

    @mobx.action
    public isFormValid = (): boolean => {
        return !this.getRecurringZoomMeetingsValidationError();
    };

    @mobx.action
    public removeZoomGroup = (zoomGroupId: number): void => {
        const filteredData = (this.entity.additionalData as IZoomGroupDTO[]).filter(group => group.zoomGroupId !== zoomGroupId);;
        this.entity.setAdditionalData(filteredData);
    };

    @mobx.action
    public setAssignedZoomGroupList = (zoomGroups: IZoomGroupDTO[]): void => {
        this.entity.setAdditionalData(zoomGroups);
    };

    public get notificationMessage(): MessageDescriptor {
        return RECURRING_ZOOM_MEETINGS_STATUS_UPDATED_TEXT;
    }

    public async apiCall(): Promise<void> {
        await this.accountAdminDAO.updateRecurringZoomMeetingsData({
            accountId: this.entity.accountId,
            type: this.entity.type,
            allowedZoomGroups: this.entity.additionalData as IZoomGroupDTO[],
            recurringZoomMeetingsEnabled: this.entity.enabled,
        });
    }

    protected shouldInterruptSubmit(): boolean {
        return super.shouldInterruptSubmit() || !this.isFormValid();
    }

    protected onInteruptHandler(): boolean | void {
        if (!this.isFormValid()) {
            this.isFormChecked = true;
        }
    }
}

export { RecurringZoomMeetingsFormVM };
